import React, { useState, useEffect } from 'react';
import { Euro, Save, Loader2, Plus, Trash2, Tv } from 'lucide-react';
import api from '../../services/api';
import PricingMatrix from './PricingMatrix';

const PricingPage = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/admin/settings/screen-pricing');
        setTiers(res.data.tiers || []);
      } catch {
        setTiers([
          { screens: 1, price: 59 },
          { screens: 2, price: 99 },
          { screens: 3, price: 129 },
          { screens: 4, price: 149 },
          { screens: 5, price: 159 },
        ]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api.post('/admin/settings/screen-pricing', { tiers });
      setMsg({ type: 'success', text: 'Tarifs sauvegardés' });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 4000);
    }
  };

  const updateTier = (idx, field, value) => {
    const updated = [...tiers];
    updated[idx] = { ...updated[idx], [field]: field === 'price' ? parseFloat(value) || 0 : parseInt(value) || 1 };
    setTiers(updated);
  };

  const addTier = () => {
    const maxScreens = tiers.length > 0 ? Math.max(...tiers.map(t => t.screens)) + 1 : 1;
    setTiers([...tiers, { screens: maxScreens, price: 0 }]);
  };

  const removeTier = (idx) => {
    setTiers(tiers.filter((_, i) => i !== idx));
  };

  if (loading) return <div className="text-[#B8B8BA] text-center py-12">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <PricingMatrix />
      <div className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D]">
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <Tv className="h-5 w-5 text-[#FF6B35]" />
          Tarification simple par nombre d'écrans (héritée)
        </h2>
        <p className="text-[#B8B8BA] text-sm mb-6">
          Configurez le prix de l'abonnement annuel selon le nombre d'écrans. Le client choisira son offre au moment de l'achat.
        </p>

        {msg && (
          <div className={`mb-4 p-3 rounded-lg border text-sm ${msg.type === 'success' ? 'bg-[#00FF88]/10 border-[#00FF88] text-[#00FF88]' : 'bg-[#FF3B5C]/10 border-[#FF3B5C] text-[#FF3B5C]'}`}>
            {msg.text}
          </div>
        )}

        <div className="space-y-3">
          {tiers.map((tier, idx) => (
            <div key={idx} className="flex items-center gap-3 bg-[#222226] p-4 rounded-lg border border-[#3A3A3D]" data-testid={`pricing-tier-${idx}`}>
              <div className="flex-1">
                <label className="text-[#6B6B6E] text-xs block mb-1">Écrans</label>
                <input
                  type="number" min="1" max="20"
                  value={tier.screens}
                  onChange={e => updateTier(idx, 'screens', e.target.value)}
                  className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 text-white text-center font-bold focus:border-[#FF6B35] focus:outline-none"
                  data-testid={`pricing-screens-${idx}`}
                />
              </div>
              <div className="flex-1">
                <label className="text-[#6B6B6E] text-xs block mb-1">Prix (EUR)</label>
                <div className="relative">
                  <input
                    type="number" min="0" step="0.01"
                    value={tier.price}
                    onChange={e => updateTier(idx, 'price', e.target.value)}
                    className="w-full bg-[#1A1A1D] border border-[#3A3A3D] rounded px-3 py-2 pr-8 text-white font-bold focus:border-[#FF6B35] focus:outline-none"
                    data-testid={`pricing-price-${idx}`}
                  />
                  <Euro className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6E]" />
                </div>
              </div>
              <button onClick={() => removeTier(idx)} className="p-2 text-[#FF3B5C] hover:bg-[#FF3B5C]/10 rounded-lg transition-colors mt-5" data-testid={`pricing-remove-${idx}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-3 mt-4">
          <button onClick={addTier} className="flex items-center gap-2 px-4 py-2 bg-[#222226] border border-[#3A3A3D] rounded-lg text-[#B8B8BA] hover:border-[#FF6B35] transition-colors text-sm" data-testid="pricing-add-tier">
            <Plus className="h-4 w-4" /> Ajouter un palier
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-[#FF6B35] text-black font-bold rounded-lg hover:bg-[#F7931E] transition-colors disabled:opacity-50 text-sm" data-testid="pricing-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Sauvegarder
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
